/**
 * «Обзор района» v2 (agroai-6al.5, план `docs/research/2026-10-03-page-plan-v2.md` §1).
 *
 * Порядок — «сначала требующее действия, справка ниже» (agroai-0e8, журнал
 * №1560–1566): 1 шапка (Цикл · Массив-фильтр · Печать) → 2 «Сейчас в районе»
 * (агроциклы и этапы, работы в поле, скоро) → 3 «Где нужно разобраться»
 * (`astra/problems/*`) → 4 пять показателей с основой «из N» → 5 «Находки»
 * сканера → 6 динамика: «Ход работ» | «Темп» 7/5 → 7 справка: «От решения к
 * результату», «Хозяйства: после срока» и «Работы по источнику» (свёрнуты).
 *
 * Факты района на одну дату (общая `as_of`) и один знаменатель — обязательства
 * выбранного цикла. Массив — только фильтр (журнал №85): нигде на странице он
 * не строка, не столбец и не ось.
 *
 * Клик по KPI «После срока» / «Вода» / «Карантин» прокручивает к блоку 3 и
 * включает вкладку типа. Адрес меняется только выбором хозяйства (`?farm=`);
 * `?kind=` читается при входе (ссылка «источник» из Поручений).
 *
 * `?mode=print` — печатный режим «К совещанию»: без фильтров и кнопок.
 */
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarClock, ClipboardList, Droplets, ListChecks, Printer, ShieldAlert } from 'lucide-react';
import { api, type QueryParams, type Schemas } from '../../api/client';
import { useRequest } from '../../api/useRequest';
import { useAnalysisContext } from '../../analysis/context';
import { useSliceDate } from '../../analysis/useSliceDate';
import { InfoPopover } from '../../components/InfoPopover';
import { RequestErrorPanel } from '../../components/ui';
import { usePageTrail } from '../shell/ShellContext';
import { ChoiceSelect } from '../ui/AnalysisControls';
import { Card, Grid } from '../ui/Card';
import { KpiCard } from '../ui/KpiCard';
import { PageHeader } from '../ui/PageHeader';
import { Sparkline } from '../ui/Spark';
import { cycleLabel, defaultCycle, previousCycle, sameDateLastYear } from '../data-source/api/compare';
import { doneShareLabel, reportShareLabel, reportShareValue } from '../lib/reportShare';
import { LoadError, failed } from '../ui/LoadError';
import { dateShort, m3Compact, num, pct, WORDS } from '../lib/format';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';
import { COMMON } from '../../i18n/common';
import { runtimeLocale, numberLocale } from '../../i18n/runtime';
import { ProblemsBoard } from '../astra/problems/ProblemsBoard';
import { KPI_TAB, tabFromParam, workName, type ProblemTab } from '../astra/problems/model';
import type { ProblemScope } from '../astra/problems/useFarmReview';
import {
  addDays,
  campaignAreaPace,
  campaignPace,
  countDelta,
  daysBetween,
  doneOf,
  hasProgressSplit,
  inProgressOf,
  isClosed,
  isLate,
  massifLabel,
  openWindows,
  rankFarmsByArea,
  seasonAreaSeries,
  seasonSeries,
  shareDelta,
  upcomingWindows,
  value,
} from './district/model';
import { DecisionFlow, Delta, EmptyBlock, LateFarms, PaceChart, SeasonChart } from './district/blocks';
import { NowSummary, type ActiveCycle, type FieldWindow, type SoonWork } from './district/NowSummary';
import { InsightsBlock } from './district/InsightsBlock';
import { Disclosure } from '../ui/Disclosure';
import { DistrictSkeleton } from './district/DistrictSkeleton';
import { Skeleton, SkeletonChart } from '../ui/Skeleton';
import { isLoading } from '../ui/AsyncBlock';
import './district/district.css';
import { waterShareMethod, waterShareWords, cycleWaterShare } from './waterSummary';
import { areaDoneDelta, areaDonePct, areaSplit, countShareText, haOf, haWhole, hasArea, noArea } from '../lib/areaWork';
import { agrocycleApi } from './agrocycle/agrocycleApi';
import { kpi, planLine } from './agrocycle/agrocycleView';
import { CycleSources } from './agrocycle/CycleSources';

type Picture = Schemas['SeasonPictureOut'];

const cycleName = cycleLabel;
const PROBLEMS_ID = 'problems';

const WEEKS = 26;
/** «Скоро»: окна, которые откроются в ближайшие дни; конец агроцикла — если он в ближайший месяц. */
const SOON_DAYS = 14;
const CYCLE_END_DAYS = 31;

const isDemo = (p: Picture | null | undefined) => !!p && (p.obligations.measured?.epistemic === 'synthetic_demo');

const m = defineMessages({
  /* --- шапка и срез ------------------------------------------------------ */
  titleMain: { id: 'district.header.title', defaultMessage: 'Обзор района', description: 'Заголовок главной страницы хокима и менеджера «Обзор района»' },
  titleMassif: { id: 'district.header.titleMassif', defaultMessage: 'Обзор района · {massif}', description: 'Заголовок «Обзора района», когда выбран массив-фильтр; {massif} — название массива из данных' },
  titlePrint: { id: 'district.header.titlePrint', defaultMessage: 'К совещанию · {title}', description: 'Заголовок печатного режима: приставка к обычному заголовку страницы; {title} — «Обзор района» (с массивом или без)' },
  trailDistrict: { id: 'district.header.trail.district', defaultMessage: 'Район', description: 'Название уровня в хлебных крошках шапки: район (верхний уровень просмотра)' },
  trailMassif: { id: 'district.header.trail.massif', defaultMessage: 'Массив', description: 'Название уровня в хлебных крошках шапки: массив (группа хозяйств внутри района)' },
  districtName: { id: 'district.header.districtName', defaultMessage: 'Бука', description: 'Название района (Бука) в хлебных крошках и в строке среза под заголовком; географическое имя — переводится транслитерацией' },
  cycleControl: { id: 'district.header.control.cycle', defaultMessage: 'Агроцикл', description: 'Подпись списка выбора агроцикла (культура × сезон) в шапке «Обзора района»; коротко' },
  massifControl: { id: 'district.header.control.massif', defaultMessage: 'Массив', description: 'Подпись списка выбора массива-фильтра в шапке «Обзора района»; коротко' },
  optionAll: { id: 'district.header.option.all', defaultMessage: 'все', description: 'Значение списка массивов: фильтр не применён, показаны все массивы района; строчными, коротко' },
  optionCurrent: { id: 'district.header.option.current', defaultMessage: 'текущий', description: 'Пометка у текущего агроцикла в списке выбора агроцикла; строчными, коротко' },
  printButton: { id: 'district.header.print', defaultMessage: 'Печать', description: 'Кнопка в шапке «Обзора района»: перейти в печатный режим «К совещанию»; коротко' },
  printNow: { id: 'district.header.printNow', defaultMessage: 'Печатать', description: 'Кнопка в печатном режиме: открыть диалог печати браузера; коротко' },
  backToOverview: { id: 'district.header.backToOverview', defaultMessage: 'К обзору', description: 'Кнопка в печатном режиме: вернуться к обычному «Обзору района»; коротко' },
  scopeCycle: {
    id: 'district.header.scope.cycle',
    defaultMessage: '{district} · {farms} в агроцикле · {land} · на {date}',
    description: 'Строка среза под заголовком «Обзора района» (хозяйства по размещению агроцикла); {district} — название района, {farms} — «24 хозяйства», {land} — земля агроцикла с единицей или «земля не измерена», {date} — дата среза',
  },
  scopeWorks: {
    id: 'district.header.scope.works',
    defaultMessage: '{district} · {farms} с работами агроцикла · {land} · на {date}',
    description: 'Строка среза под заголовком «Обзора района» (хозяйства по запущенным работам); {district} — название района, {farms} — «24 хозяйства», {land} — земля под работами с единицей или «площадь не измерена», {date} — дата среза',
  },
  scopePrint: { id: 'district.header.scope.print', defaultMessage: '{name} · {scope}', description: 'Строка среза в печатном режиме: впереди имя агроцикла; {name} — «Хлопок 2026», {scope} — готовая строка среза' },
  landCycle: { id: 'district.header.land.cycle', defaultMessage: '{ha} га земли агроцикла', description: 'Земля агроцикла в строке среза; {ha} — число гектаров, уникальная земля (каждый гектар один раз)' },
  landCycleNone: { id: 'district.header.land.cycleNone', defaultMessage: 'земля не измерена', description: 'Вместо земли агроцикла в строке среза, когда площадь не измерена; честное «нет данных», а не 0' },
  landWorks: { id: 'district.header.land.works', defaultMessage: '{ha} га земли под работами агроцикла', description: 'Земля под работами агроцикла в строке среза; {ha} — число гектаров, уникальная земля' },
  landWorksNone: { id: 'district.header.land.worksNone', defaultMessage: 'площадь не измерена', description: 'Вместо земли под работами в строке среза, когда площадь не измерена; честное «нет данных», а не 0' },
  scopeInfoLabel: { id: 'district.header.scopeInfo.label', defaultMessage: 'Срез обзора', description: 'aria-label кнопки «i» у строки среза под заголовком «Обзора района»' },
  scopeInfoFarmsLand: {
    id: 'district.header.scopeInfo.farmsLand',
    defaultMessage: 'Хозяйства и земля агроцикла — по размещению сезона (поля агроцикла, каждый гектар один раз), а не по запущенным работам.',
    description: 'Подсказка «i» у строки среза, первое предложение (показывается, когда известны хозяйства и земля по размещению агроцикла)',
  },
  scopeInfoMain: {
    id: 'district.header.scopeInfo.main',
    defaultMessage:
      'Ход работ считается по площади и только по работам на полях запущенных кампаний плана агроцикла: знаменатель «Выполнено», «После срока» и кампании — гектары работ агроцикла {name} на дату среза {date} (у каждой работы на поле вес — площадь самой работы на поле (план — площадь размещения, карантин — земля контура, полив — доля посева)); рядом мелко — то же по числу работ. Предписания карантина и добровольные работы — своими строками, в долю не входят.',
    description: 'Подсказка «i» у строки среза: как считается ход работ; {name} — имя агроцикла («Хлопок 2026»), {date} — дата среза',
  },
  scopeInfoChartsArea: { id: 'district.header.scopeInfo.chartsArea', defaultMessage: 'Графики хода и темпа — в гектарах работ.', description: 'Подсказка «i» у строки среза: графики построены по площади работ' },
  scopeInfoChartsCount: { id: 'district.header.scopeInfo.chartsCount', defaultMessage: 'Графики хода и темпа — по числу работ: ряда в гектарах сервер для этого среза не дал.', description: 'Подсказка «i» у строки среза: графики построены по числу работ, потому что нет ряда по площади' },
  scopeInfoTail: {
    id: 'district.header.scopeInfo.tail',
    defaultMessage: 'Внесённый отчёт считается фактом. Отсутствие отчёта не означает, что работа не сделана. Фильтр массива сужает показатели и графики; список хозяйств «после срока» остаётся районным.',
    description: 'Подсказка «i» у строки среза, заключительные предложения: что такое факт и что делает фильтр массива',
  },

  /* --- общие куски KPI --------------------------------------------------- */
  perWeek: { id: 'district.kpi.perWeek', defaultMessage: 'к неделе', description: 'Подпись после изменения показателя в KPI: сравнение с тем же показателем неделю назад («+1,5 п.п. к неделе»); коротко' },
  perWeekFailed: { id: 'district.kpi.perWeekFailed', defaultMessage: 'к неделе: не загрузилось', description: 'Вместо изменения к неделе в KPI, когда срез недельной давности не загрузился; коротко' },
  ppUnit: { id: 'district.kpi.ppUnit', defaultMessage: 'п.п.', description: 'Единица «процентные пункты» после изменения доли («+1,5 п.п.»); коротко' },
  ppValue: { id: 'district.kpi.ppValue', defaultMessage: '{value} п.п.', description: 'Изменение доли в процентных пунктах со знаком; {value} — число со знаком («+1,5»)' },
  haUnit: { id: 'district.kpi.haUnit', defaultMessage: 'га', description: 'Единица «гектары» после изменения показателя («−3 га к неделе»); коротко' },
  noData: { id: 'district.kpi.noData', defaultMessage: 'нет данных', description: 'Вместо числа в тексте подсказки, когда данных для сравнения нет' },
  sparkLabel: { id: 'district.kpi.sparkLabel', defaultMessage: 'Накопленная доля выполненного по неделям, {unit}', description: 'Подпись полоски-графика в KPI «Выполнено» для чтеца с экрана; {unit} — «по площади работ» или «по числу работ»' },
  sparkByArea: { id: 'district.kpi.sparkByArea', defaultMessage: 'по площади работ', description: 'Уточнение, как считалась полоска: по площади работ; строчными, встаёт в конец фразы' },
  sparkByCount: { id: 'district.kpi.sparkByCount', defaultMessage: 'по числу работ', description: 'Уточнение, как считалась полоска: по числу работ; строчными, встаёт в конец фразы' },
  worksWord: {
    id: 'district.kpi.worksWord',
    defaultMessage: '{count, plural, one {работа на поле} few {работы на полях} many {работ на полях} other {работы на полях}}',
    description: 'Слово-единица рядом с числом в KPI: «312 работ на полях»; без числа, склоняется по {count}',
  },
  worksOfTotal: {
    id: 'district.kpi.worksOfTotal',
    defaultMessage: '{done} из {total, plural, one {# работы на поле} few {# работ на полях} many {# работ на полях} other {# работы на полях}}',
    description: 'Часть из целого в числе работ на полях: «312 из 1 145 работ на полях»; {done} — готовое число, {total} — всего',
  },

  /* --- KPI «Выполнено» ---------------------------------------------------- */
  doneUnitArea: { id: 'district.kpi.done.unitArea', defaultMessage: '% площади работ', description: 'Единица главного числа KPI «Выполнено»: доля выполненной площади работ; коротко, мелким шрифтом рядом с числом' },
  doneUnitCount: { id: 'district.kpi.done.unitCount', defaultMessage: 'работ', description: 'Единица главного числа KPI «Выполнено», когда площади нет и доля по числу работ (после «78,5 %»); коротко' },
  doneNoteOf: { id: 'district.kpi.done.noteOf', defaultMessage: '{done} из {total} га работ', description: 'Основа KPI «Выполнено» (режим по площади): гектары выполненных работ из гектаров всех работ запущенных кампаний; {done}/{total} — готовые числа' },
  doneNoteCountOf: {
    id: 'district.kpi.done.noteCountOf',
    defaultMessage: '{done} из {total, plural, one {# работы на поле} few {# работ на полях} many {# работ на полях} other {# работы на полях}}',
    description: 'Основа KPI «Выполнено» (режим по числу, площадь не указана): «312 из 1 145 работ на полях»; {done} — готовое число, {total} — всего',
  },
  doneInfoLabelArea: { id: 'district.kpi.done.infoLabel', defaultMessage: 'Как считается «{label}»', description: 'aria-label кнопки «i» у KPI «Выполнено»/«Доля отчётов»; {label} — название KPI' },
  doneInfoAreaMain: {
    id: 'district.kpi.done.info.areaMain',
    defaultMessage:
      'Выполнено {share} площади работ агроцикла {name} на {date}: Σ га выполненных работ / Σ га всех работ агроцикла ({done} из {total} га работ). В долю входят только работы на полях запущенных кампаний плана{hasPlan, select, yes { ({plan})} other {}}: незапущенных работ в знаменателе нет, поэтому доля — не «сезон пройден на столько-то»; предписания и добровольные работы — отдельными строками ниже. У каждой работы вес — площадь самой работы на поле (план — площадь размещения, карантин — земля контура, полив — доля посева) из реестра; поле с несколькими работами входит столько раз, сколько на нём работ. «Выполнено» — внесён отчёт «выполнено» (факт без проверки); «в процессе» ({inProgress} площади работ) в долю не входит; без отчёта — {noReport}. По числу работ на полях — {worksDone}{countShare, select, none {} other {, {countShare}}}. Изменение к неделе — та же доля площади на {weekDate}.',
    description: 'Подсказка «i» у KPI «Выполнено» (режим по площади): как считается доля; {share}/{inProgress}/{noReport} — проценты, {name} — имя агроцикла, {date}/{weekDate} — даты, {done}/{total} — гектары работ, {plan} — строка плана («запущено 3 из 8 работ плана · впереди 5»), {hasPlan} — yes/no, {worksDone} — «312 из 1 145 работ на полях», {countShare} — «77,2 % по числу работ» или none',
  },
  doneInfoPrevCycle: {
    id: 'district.kpi.done.info.prevCycle',
    defaultMessage: 'К {cycle} на {date}: {value}{delta, select, none {} other { ({delta})}}.',
    description: 'Подсказка «i» у KPI «Выполнено»: сравнение с прошлым агроциклом той же культуры на ту же дату; {cycle} — имя прошлого агроцикла, {value} — «78 % площади работ» или «нет данных», {delta} — «+1,5 п.п.» или none',
  },
  doneInfoPrevValueArea: { id: 'district.kpi.done.info.prevValueArea', defaultMessage: '{share} площади работ', description: 'Значение прошлого агроцикла в тексте подсказки KPI «Выполнено»; {share} — готовый процент' },
  doneInfoNoPrev: { id: 'district.kpi.done.info.noPrev', defaultMessage: 'Прошлого агроцикла этой культуры в срезе нет.', description: 'Подсказка «i» у KPI «Выполнено»: нечего сравнивать с прошлым сезоном' },
  doneInfoSpark: { id: 'district.kpi.done.info.spark', defaultMessage: 'Полоска — накопленная доля по неделям, {unit}.', description: 'Подсказка «i» у KPI «Выполнено», последнее предложение; {unit} — «по площади работ» или «по числу работ»' },
  doneInfoCountNoArea: { id: 'district.kpi.done.info.noArea', defaultMessage: 'Площадь работ агроцикла не указана, поэтому доля — по числу работ.', description: 'Подсказка «i» у KPI «Выполнено» (режим по числу): первое предложение, почему доля не по площади' },
  doneInfoCountSplit: {
    id: 'district.kpi.done.info.countSplit',
    defaultMessage: 'Выполнено {done} из {total, plural, one {# работы на поле} few {# работ на полях} many {# работ на полях} other {# работы на полях}} агроцикла на {date} — по ним внесён отчёт «выполнено». Ещё {inProgress} — в процессе. Нет отчёта — {noReport}.',
    description: 'Подсказка «i» у KPI «Выполнено» (режим по числу, есть разделение «выполнено/в процессе»); {done}/{inProgress}/{noReport} — готовые числа, {total} — всего работ на полях, {date} — дата среза',
  },
  doneInfoCountReported: {
    id: 'district.kpi.done.info.countReported',
    defaultMessage: '{reported} из {total, plural, one {# работы на поле} few {# работ на полях} many {# работ на полях} other {# работы на полях}} агроцикла с отчётом на {date}.',
    description: 'Подсказка «i» у KPI «Доля отчётов» (режим по числу, без разделения по степени); {reported} — готовое число, {total} — всего работ на полях, {date} — дата среза',
  },

  /* --- KPI «После срока» -------------------------------------------------- */
  overdueTitle: { id: 'district.kpi.overdue.title', defaultMessage: 'После срока', description: 'Название KPI: земля (или работы), где окно работы закрылось, а отчёта нет; коротко, в карточке ряда из шести KPI' },
  overdueUnitLand: { id: 'district.kpi.overdue.unitLand', defaultMessage: 'га земли', description: 'Единица главного числа KPI «После срока»: гектары уникальной земли (не «га работ»); коротко' },
  overdueActionLabel: { id: 'district.kpi.overdue.actionLabel', defaultMessage: 'После срока: открыть разбор по срокам', description: 'aria-label KPI «После срока»: нажатие прокручивает к разбору хозяйств по срокам' },
  overdueNoteLandOf: { id: 'district.kpi.overdue.noteLandOf', defaultMessage: 'из {land} га земли под работами · {share}', description: 'Основа KPI «После срока»: из скольких гектаров земли под работами агроцикла (каждый гектар один раз) и доля; {land} — гектары земли, {share} — готовый процент' },
  overdueNoteNoReport: { id: 'district.kpi.overdue.noteNoReport', defaultMessage: 'без отчёта · {noArea}', description: 'Мелкая подпись KPI «После срока», когда площади нет и число считано по работам; {noArea} — «площадь не указана»' },
  overdueInfoLabel: { id: 'district.kpi.overdue.infoLabel', defaultMessage: 'Как считается «После срока»', description: 'aria-label кнопки «i» у KPI «После срока»' },
  overdueInfoHead: { id: 'district.kpi.overdue.info.head', defaultMessage: 'Окно работы закрылось, отчёта нет.', description: 'Подсказка «i» у KPI «После срока», первое предложение' },
  overdueInfoLand: {
    id: 'district.kpi.overdue.info.land',
    defaultMessage:
      'Число — ЗЕМЛЯ, где есть хотя бы одна такая работа, каждый гектар один раз: {ha} га{hasTotal, select, yes { из {land} га земли под работами агроцикла} other {}}{hasShare, select, yes { ({share})} other {}}. Мелко — число таких работ ({works}) и гектары работ{hasWorkHa, select, yes { ({workHa})} other {}}: сумма площадей работ, где поле с тремя работами после срока считается трижды, поэтому она бывает больше всей земли агроцикла.',
    description: 'Подсказка «i» у KPI «После срока» (режим по земле); {ha} — гектары земли с просрочкой, {land} — вся земля под работами агроцикла, {share} — процент, {works} — число просроченных работ на полях, {workHa} — гектары работ; {hasTotal}/{hasShare}/{hasWorkHa} — yes/no',
  },
  overdueInfoCount: { id: 'district.kpi.overdue.info.count', defaultMessage: 'Площадь не указана, поэтому число — работы на полях.', description: 'Подсказка «i» у KPI «После срока», когда площади нет и число считано по работам' },
  overdueInfoTail: { id: 'district.kpi.overdue.info.tail', defaultMessage: 'Нет отчёта — повод уточнить, а не вывод «не сделано». Нажмите карточку — откроется разбор хозяйств по срокам.', description: 'Подсказка «i» у KPI «После срока», последние предложения' },

  /* --- KPI «Вода» --------------------------------------------------------- */
  waterTitle: { id: 'district.kpi.water.title', defaultMessage: 'Вода', description: 'Название KPI: доля поданной воды от запрошенной; коротко' },
  waterActionLabel: { id: 'district.kpi.water.actionLabel', defaultMessage: 'Вода: открыть разбор по воде', description: 'aria-label KPI «Вода»: нажатие прокручивает к разбору по воде' },
  waterInfoLabel: { id: 'district.kpi.water.infoLabel', defaultMessage: 'Как считается «Вода»', description: 'aria-label кнопки «i» у KPI «Вода»' },
  waterNoteDoneLand: { id: 'district.kpi.water.noteDoneLand', defaultMessage: 'подано по файлу ирригации, заявок нет · полито {pct} площади поливов с наступившим окном', description: 'Основа KPI «Вода» (заявок нет, вода по файлу ирригации): доля политой площади поливов, у которых окно уже наступило; {pct} — процент или «—»' },
  waterInfoEvents: { id: 'district.kpi.water.infoEvents', defaultMessage: 'Σ м³ событий файла ирригации, закрывших поливы плана агроцикла. Заявок на воду в данных нет, поэтому «запрошено» и недоподача не считаются. Доля — политая земля пар «контур × полив» с наступившим окном; подробности — на экране «Вода».', description: 'Пояснение «i» у KPI «Вода», когда вода считается по событиям файла ирригации' },
  waterNoteUnknown: { id: 'district.kpi.water.noteUnknown', defaultMessage: '{words} · недоподача —', description: 'Подпись KPI «Вода», когда недоподача неизвестна; {words} — «подано от запрошенного»' },
  waterNoteDeficit: { id: 'district.kpi.water.noteDeficit', defaultMessage: '{words} · недоподача {m3}', description: 'Подпись KPI «Вода» с предупреждением; {words} — «подано от запрошенного», {m3} — объём недоподачи с единицей («1,2 млн м³»)' },
  waterNoteNone: { id: 'district.kpi.water.noteNone', defaultMessage: '{words} · недоподачи нет', description: 'Подпись KPI «Вода», когда недоподачи нет; {words} — «подано от запрошенного»' },

  /* --- KPI «Карантин» ----------------------------------------------------- */
  quarantineTitle: { id: 'district.kpi.quarantine.title', defaultMessage: 'Карантин', description: 'Название KPI: контуры с красным или жёлтым карантинным статусом; коротко' },
  quarantineUnit: {
    id: 'district.kpi.quarantine.unit',
    defaultMessage: '{count, plural, one {контур} few {контура} many {контуров} other {контура}}',
    description: 'Единица главного числа KPI «Карантин»: «20 контуров»; без числа, склоняется по {count}',
  },
  quarantineActionLabel: { id: 'district.kpi.quarantine.actionLabel', defaultMessage: 'Карантин: открыть разбор по карантину', description: 'aria-label KPI «Карантин»: нажатие прокручивает к разбору по карантину' },
  quarantineNoteOf: { id: 'district.kpi.quarantine.noteOf', defaultMessage: 'из {total} · {red} красн. · {yellow} жёлт.', description: 'Основа KPI «Карантин»: из скольких контуров района и сколько красных и жёлтых; {total}/{red}/{yellow} — числа; узкая карточка, сокращения оставить короткими' },
  quarantineLoadError: { id: 'district.kpi.quarantine.loadError', defaultMessage: 'ошибка загрузки статусов', description: 'Подпись KPI «Карантин», когда статусы не загрузились; строчными' },
  quarantineInfoLabel: { id: 'district.kpi.quarantine.infoLabel', defaultMessage: 'Как считается «Карантин»', description: 'aria-label кнопки «i» у KPI «Карантин»' },
  quarantineInfo: { id: 'district.kpi.quarantine.info', defaultMessage: 'Контуры с красным или жёлтым статусом по последнему событию карантина на дату среза. Без событий контур считается зелёным.', description: 'Подсказка «i» у KPI «Карантин»: как считается число контуров' },

  /* --- KPI «Поручения» ---------------------------------------------------- */
  assignTitle: { id: 'district.kpi.assign.title', defaultMessage: 'Поручения', description: 'Название KPI: открытые поручения хозяйствам; коротко' },
  assignUnitOpenOf: { id: 'district.kpi.assign.unitOpenOf', defaultMessage: 'открыто из {total}', description: 'Единица главного числа KPI «Поручения»: «12 открыто из 15» — открытые из всех поручений района; {total} — число; строчными, коротко' },
  assignUnitFailed: { id: 'district.kpi.assign.unitFailed', defaultMessage: 'не загрузилось', description: 'Вместо единицы KPI «Поручения», когда список не загрузился; строчными, коротко' },
  assignLate: { id: 'district.kpi.assign.late', defaultMessage: '{count} после срока', description: 'Подпись KPI «Поручения»: сколько открытых поручений просрочено; {count} — число' },
  assignInfoLabel: { id: 'district.kpi.assign.infoLabel', defaultMessage: 'Как считаются поручения', description: 'aria-label кнопки «i» у KPI «Поручения»' },
  assignInfoOpen: {
    id: 'district.kpi.assign.infoOpen',
    defaultMessage: 'Открытые — все поручения района, кроме закрытых (результат проверен). «После срока» — срок прошёл, поручение не закрыто. Шаги от назначения до проверки и эффект поручения — в «От решения к результату» ниже и на экране «Поручения».',
    description: 'Подсказка «i» у KPI «Поручения»: что значит «открытые» и «после срока», где смотреть шаги и эффект',
  },

  /* --- разделы страницы --------------------------------------------------- */
  sectionKpi: { id: 'district.section.kpi', defaultMessage: 'Показатели · {name} · на {date}', description: 'Заголовок раздела с пятью показателями на «Обзоре района» (под разбором проблем); {name} — имя агроцикла, {date} — дата среза' },
  sectionDynamics: { id: 'district.section.dynamics', defaultMessage: 'Динамика', description: 'Заголовок раздела графиков «Ход работ сезона» и «Темп кампании» на «Обзоре района»' },
  sectionRef: { id: 'district.section.reference', defaultMessage: 'Справка', description: 'Заголовок нижнего раздела «Обзора района»: шаги поручений, хозяйства после срока, работы по источнику' },
  sourcesSummary: { id: 'district.sources.summary', defaultMessage: 'план, предписания карантина и добровольные — раздельно', description: 'Краткая подпись свёрнутого блока «Работы агроцикла по источнику»; строчными' },

  /* --- блок «Работы агроцикла по источнику» ------------------------------- */
  sourcesTitle: { id: 'district.sources.title', defaultMessage: 'Работы агроцикла по источнику', description: 'Заголовок карточки на «Обзоре района»: работы агроцикла по источнику (план, предписания, добровольные)' },
  sourcesTitleDistrict: { id: 'district.sources.titleDistrict', defaultMessage: 'Работы агроцикла по источнику · по району', description: 'Заголовок карточки «Работы агроцикла по источнику», когда выбран массив: строка остаётся районной' },
  sourcesInfo: {
    id: 'district.sources.info',
    defaultMessage:
      'План, предписания карантина и добровольные работы считаются раздельно (ось «источник работы» слоя метрик). В «Выполнено» входят только работы на полях запущенных кампаний плана; предписания — свой счёт; добровольные — учёт хозяйств без доли выполнения и без сравнения хозяйств. Пустое — записей нет, а не ноль.',
    description: 'Подсказка «i» карточки «Работы агроцикла по источнику»: чем различаются план, предписания и добровольные работы',
  },

  /* --- состояния страницы ------------------------------------------------- */
  loadFailedTitle: { id: 'district.state.loadFailed', defaultMessage: 'Обзор района не загрузился', description: 'Заголовок ошибки загрузки страницы «Обзор района» (хоким, менеджер)' },
  noCycles: { id: 'district.state.noCycles', defaultMessage: 'Агроциклов в срезе нет.', description: 'Пустая страница «Обзор района»: для выбранного среза нет ни одного агроцикла' },
  seasonNoSummary: { id: 'district.season.noSummary', defaultMessage: 'Сводка недель не пришла в ответе сервера.', description: 'Причина ошибки в карточке «Ход работ сезона»: сервер не вернул недельную сводку' },
  paceTitle: { id: 'district.pace.cardTitle', defaultMessage: 'Темп кампании · {title}', description: 'Заголовок карточки «Темп кампании» в состояниях загрузки и ошибки; {title} — название работы кампании' },
  paceTitlePlain: { id: 'district.pace.cardTitlePlain', defaultMessage: 'Темп кампании', description: 'Заголовок карточки «Темп кампании», когда открытой кампании нет' },
  paceWhat: { id: 'district.pace.what', defaultMessage: 'дневной ряд кампании', description: 'Что не загрузилось (винительный падеж, строчными): «Не удалось загрузить дневной ряд кампании»' },
  paceSkeleton: { id: 'district.pace.skeleton', defaultMessage: 'темп кампании', description: 'Подпись заглушки загрузки графика «Темп кампании» для чтеца с экрана; строчными' },
  paceNoCampaign: { id: 'district.pace.noCampaign', defaultMessage: 'Открытых кампаний на дату среза нет.', description: 'Пояснение в пустой карточке «Темп кампании»: на дату среза нет открытой кампании' },
  footScope: { id: 'district.foot.scope', defaultMessage: 'Срез: {name} · на {date}.', description: 'Нижняя строка страницы «Обзор района»: агроцикл и дата среза; {name} — имя агроцикла, {date} — дата' },
  footScopeDemo: { id: 'district.foot.scopeDemo', defaultMessage: 'Срез: {name} · на {date} · демонстрационные данные.', description: 'Нижняя строка страницы «Обзор района» на демонстрационных данных; {name} — имя агроцикла, {date} — дата' },
});

const intlLocale = () => {
  return numberLocale();
};
/** Число с заданной точностью на языке интерфейса (для значений KPI без знака процента). */
const fmtNum = (value: number, min: number, max = min) =>
  new Intl.NumberFormat(intlLocale(), { minimumFractionDigits: min, maximumFractionDigits: max }).format(value);

export function DistrictOverview() {
  const t = useT();
  const ctx = useAnalysisContext();
  const [search, setSearch] = useSearchParams();
  const print = search.get('mode') === 'print';
  const [tab, setTab] = useState<ProblemTab>(() => tabFromParam(search.get('kind')));
  const base: QueryParams = ctx.params;
  const massifId = ctx.view.massifId;

  const setParam = useCallback((key: string, next: string | null) =>
    setSearch(prev => {
      const p = new URLSearchParams(prev);
      if (next === null) p.delete(key);
      else p.set(key, next);
      return p;
    }, { replace: true }), [setSearch]);

  /* --- срез: цикл и справочники ------------------------------------- */
  const cycles = useRequest(() => api.seasonCycles(base), [ctx.key]);
  const massifs = useRequest(() => api.massifs(base), [ctx.key]);
  const cycleList = cycles.data?.items ?? [];
  const cycleId = search.get('cycle') ?? defaultCycle(cycleList);
  const cycle = cycleList.find(c => c.cycle === cycleId) ?? null;
  const prevCycle = previousCycle(cycleList, cycle);
  // Строка агроцикла (слой метрик, design §7.3): участники и земля по
  // размещению, «запущено K из M работ плана · впереди N», работы по источнику.
  const agro = useRequest(
    () => (cycle ? agrocycleApi.list({ ...base, season: cycle.season_label, crop: cycle.crop_code }) : Promise.resolve(null)),
    [ctx.key, cycle?.cycle],
  );
  const agroRow = agro.data?.items.find(row => row.key === cycle?.cycle) ?? agro.data?.items[0] ?? null;
  const scoped =(extra: QueryParams = {}): QueryParams => ({ ...base, ...(massifId !== null ? { massif_id: massifId } : {}), ...extra });

  /* --- картина района (разрез по хозяйствам) и среза ----------------- */
  const district = useRequest<Picture | null>(
    () => (cycleId ? api.seasonPicture({ ...base, cycle: cycleId, summary: true, weeks: WEEKS }) : Promise.resolve(null)),
    [ctx.key, cycleId],
  );
  const massifPicture = useRequest<Picture | null>(
    () => (cycleId && massifId !== null ? api.seasonPicture(scoped({ cycle: cycleId, summary: true, weeks: WEEKS })) : Promise.resolve(null)),
    [ctx.key, cycleId, massifId],
  );
  const pic = massifId !== null ? massifPicture.data : district.data;
  const asOf = ctx.asOf ?? district.data?.horizon ?? null;
  const sliceDate = useSliceDate();
  const weekAgoDate = asOf ? addDays(asOf, -7) : null;

  const weekAgo = useRequest<Picture | null>(
    () => (cycleId && weekAgoDate ? api.seasonPicture(scoped({ cycle: cycleId, as_of: weekAgoDate })) : Promise.resolve(null)),
    [ctx.key, cycleId, massifId, weekAgoDate],
  );
  // Прошлый сезон — тот же цикл культуры на ту же календарную дату годом раньше.
  const prevAsOf = prevCycle && asOf ? sameDateLastYear(asOf) : null;
  const previous = useRequest<Picture | null>(
    () => (prevCycle && prevAsOf ? api.seasonPicture(scoped({ cycle: prevCycle.cycle, as_of: prevAsOf, summary: true, weeks: WEEKS })) : Promise.resolve(null)),
    [ctx.key, prevCycle?.cycle, prevAsOf, massifId],
  );
  const campaign = pic?.open_campaigns[0] ?? null;
  const daily = useRequest<Picture | null>(
    () => (cycleId && campaign ? api.seasonPicture(scoped({ cycle: cycleId, campaign_id: campaign.campaign_id, summary: true, daily: true, weeks: 1 })) : Promise.resolve(null)),
    [ctx.key, cycleId, campaign?.campaign_id, massifId],
  );

  /* --- карантин и поручения ------------------------------------------ */
  const quarantine = useRequest(() => api.quarantineStatuses(scoped({ limit: 1 })), [ctx.key, massifId]);
  const quarantineWeekAgo = useRequest(
    () => (weekAgoDate ? api.quarantineStatuses({ ...scoped({ limit: 1 }), as_of: weekAgoDate }) : Promise.resolve(null)),
    [ctx.key, massifId, weekAgoDate],
  );
  // «С эффектом» с карточки снят (журнал №1563): считался по diff только первых
  // десяти поручений — число без основы «из N». Эффект — на экране «Поручения».
  const assignments = useRequest(() => api.assignments(base), [ctx.key]);

  const massifName = massifId !== null ? massifLabel(massifs.data?.items.find(m => m.id === massifId)?.canonical_name) : null;
  usePageTrail(massifName ? [{ level: t(m.trailDistrict), name: t(m.districtName), to: '/' }, { level: t(m.trailMassif), name: massifName }] : [{ level: t(m.trailDistrict), name: t(m.titleMain) }]);

  /* --- расчёты ------------------------------------------------------ */
  const summary = pic?.summary ?? null;
  // Ход сезона в гектарах работ — готовый ряд сервера (G1, бывш. 1ed); без
  // площади — прежний ряд по числу работ.
  const areaSeries = useMemo(
    () => (pic?.summary && asOf ? seasonAreaSeries({ weekly: pic.summary.weekly, asOf }, previous.data?.summary ? { weekly: previous.data.summary.weekly } : null) : null),
    [pic, previous.data, asOf],
  );
  const series = useMemo(() => {
    if (areaSeries) return areaSeries;
    if (!pic?.summary || !asOf) return [];
    const prev = previous.data?.summary ? { weekly: previous.data.summary.weekly, reported: previous.data.obligations.reported, total: previous.data.obligations.total } : null;
    const prevPic = previous.data;
    const prevSplit = prevPic?.summary ? { weekly: prevPic.summary.weekly, reported: prevPic.obligations.reported, done: prevPic.obligations.done ?? null, total: prevPic.obligations.total } : prev;
    return seasonSeries({ weekly: pic.summary.weekly, reported: pic.obligations.reported, done: pic.obligations.done ?? null, total: pic.obligations.total, asOf }, prevSplit);
  }, [areaSeries, pic, previous.data, asOf]);
  const ranking = useMemo(() => rankFarmsByArea(district.data?.summary?.by_farm ?? []), [district.data]);
  // Темп кампании — в гектарах работ, когда сервер дал ряд площади (G1).
  const pace = campaign && asOf
    ? campaignAreaPace(campaign, daily.data?.summary?.daily, asOf) ?? campaignPace(campaign, daily.data?.summary?.daily, asOf)
    : null;
  const water = useMemo(() => cycleWaterShare(pic), [pic]);
  const deficit = water?.shortfallM3 ?? null;
  // Заявок нет — вода по событиям файла ирригации: подано м³ и доля политой земли по парам.
  const waterEvents = water !== null && water.requestedM3 === null && water.deliveredM3 !== null;
  const waterFile = useRequest(
    () => (waterEvents && cycle ? api.waterDistrict({ ...scoped(), cycle: cycle.cycle }) : Promise.resolve(null)),
    [ctx.key, cycle?.cycle, waterEvents, massifId],
  );
  const doneLand = waterFile.data?.metrics.find(x => x.id === 'water.irrigation_done_land_pct')?.value ?? null;
  const waterGapByFarm = useMemo(() => {
    const out = new Map<number, number>();
    // Разрыв хозяйства — с сервера (решение №222: без единой подачи — вся заявка).
    for (const r of district.data?.summary?.water_by_actor ?? []) {
      if (r.gap_m3 === null) continue;
      const gap = Number(r.gap_m3);
      if (gap > 0) out.set(r.actor_id, gap);
    }
    return out;
  }, [district.data]);
  const qCount = (d: typeof quarantine.data) => {
    if (!d) return null;
    const by = (k: string) => d.by_status.find(s => s.key === k)?.count ?? 0;
    return { red: by('red'), yellow: by('yellow') };
  };
  const qNow = qCount(quarantine.data);
  const qThen = qCount(quarantineWeekAgo.data);
  // «Сегодня» для запасного правила — дата среза, а не часы браузера. Строка поручения
  // и так приходит с готовым `overdue` (считает сервер); дата нужна только ответу без него.
  const today = asOf ?? sliceDate;
  const assignmentItems = assignments.data?.items ?? null;
  const openAssignments = assignmentItems?.filter(a => !isClosed(a)) ?? [];
  const flowCounts = useMemo(() => {
    if (!assignments.data) return null;
    const out: Record<string, number> = {};
    for (const b of assignments.data.by_status ?? []) out[b.status] = b.count;
    // Сервер без разреза по статусам — считаем по списку.
    if (!assignments.data.by_status?.length) for (const a of assignments.data.items) out[a.status] = (out[a.status] ?? 0) + 1;
    return out;
  }, [assignments.data]);

  /* --- «Сейчас в районе»: агроциклы, окна в поле, скоро -------------- */
  // Начатые агроциклы района и их этап — с сервера (`/season/cycles`, этап по
  // окнам плана); выбранный агроцикл всегда в списке, даже если он закрыт.
  const activeCycles = useMemo<ActiveCycle[]>(
    () => cycleList
      .filter(c => c.status === 'active' || c.cycle === cycleId)
      .map(c => ({ key: c.cycle, name: cycleName(c), stage: c.stage?.name ?? null, windowTo: c.window_to ?? null })),
    [cycleList, cycleId],
  );
  const fieldWindows = useMemo<FieldWindow[]>(() => {
    if (!summary || !asOf || !cycleId) return [];
    return openWindows(summary.works, asOf).slice(0, 6).map(w => {
      const ob = w.totals.obligations;
      const area = areaSplit(w.area_work);
      const doneCount = doneOf(ob);
      return {
        key: String(w.campaign_id),
        name: workName(w.name),
        windowTo: w.window_to,
        daysLeft: w.window_to ? daysBetween(asOf, w.window_to) : null,
        // area.reported — выполнено + в процессе (га работ).
        area: area && area.total > 0 ? { done: Math.max(0, area.reported - (area.inProgress ?? 0)), inProgress: area.inProgress ?? 0, total: area.total } : null,
        count: { done: doneCount, inProgress: inProgressOf(ob) ?? 0, total: ob.total },
        href: `/cycles/${cycleId}/works/${w.campaign_id}${ctx.linkSearch()}`,
      };
    });
  }, [summary, asOf, cycleId, ctx]);
  const soonWorks = useMemo<SoonWork[]>(() => {
    if (!summary || !asOf || !cycleId) return [];
    return upcomingWindows(summary.works, asOf, SOON_DAYS).slice(0, 5).map(w => ({
      key: String(w.campaign_id),
      name: workName(w.name),
      from: w.window_from!,
      href: `/cycles/${cycleId}/works/${w.campaign_id}${ctx.linkSearch()}`,
    }));
  }, [summary, asOf, cycleId, ctx]);

  const problemScope = useMemo<ProblemScope | null>(
    () => (cycleId && asOf ? { params: base, cycle: cycleId, asOf, linkSearch: ctx.linkSearch } : null),
    [base, cycleId, asOf, ctx.linkSearch],
  );
  const openProblems = (next: ProblemTab) => {
    setTab(next);
    document.getElementById(PROBLEMS_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /* --- состояния загрузки ------------------------------------------- */
  // Массив выбран, а его картина упала — та же ошибка, а не вечное «Загружаю»
  // (agroai-6al.35): без этого недоступный сервер выглядел как долгая загрузка.
  const massifFailed = massifId !== null && failed(massifPicture);
  if (cycles.state === 'error' || district.state === 'error' || massifFailed) {
    return <div className="district"><RequestErrorPanel error={cycles.error ?? district.error ?? massifPicture.error} title={t(m.loadFailedTitle)} action={<button className="astra-button" onClick={() => { cycles.reload(); district.reload(); massifPicture.reload(); }}>{t(COMMON.retry)}</button>} /></div>;
  }
  if (!cycle || !district.data || !pic || !asOf || !problemScope) {
    // Пусто — только когда список циклов пришёл и он пуст; до того — скелетон
    // страницы в размерах готовой раскладки (6al.49), а не строка «Загружаю».
    if (cycles.data && !cycleList.length) return <div className="district"><p className="district-foot">{t(m.noCycles)}</p></div>;
    return <DistrictSkeleton />;
  }

  const demo = isDemo(pic);
  const name = cycleName(cycle);
  const link = (path: string, extra?: Record<string, string | number>) => `${path}${ctx.linkSearch(extra)}`;
  const ob = pic.obligations;
  // «Выполнено» — главный KPI (журнал №290): финальный отчёт «выполнено».
  // Сервер без разреза по степени — прежняя ось «отчитались».
  const split = hasProgressSplit(ob);
  const done = doneOf(ob);
  const inProgress = inProgressOf(ob) ?? 0;
  const noReport = Math.max(0, ob.total - ob.reported);
  const week = shareDelta(ob, weekAgo.data?.obligations);
  const vsPrev = shareDelta(ob, previous.data?.obligations);
  const prevShare = previous.data ? shareDelta(previous.data.obligations, null).share : null;
  const overdueNow = value(pic.overdue);
  // Пара «счёт + земля» (07.10.2026): 13 % обязательств после срока на demo4 —
  // это 73 % земли цикла, где хотя бы одна работа после срока. Одно число без
  // другого искажает картину в ту или другую сторону.
  const overdueHa = value(pic.overdue_area) === null ? null : value(pic.overdue_area)! / 1e6;
  // Площадь работ (решение №645): главный «Выполнено» — % площади, рядом
  // мелко — по числу работ. Нет площади — честно «площадь не указана», и
  // карточка остаётся на счёте.
  const aw = pic.area_work ?? null;
  const areaPct = areaDonePct(aw);
  const areaWeek = areaDoneDelta(aw, weekAgo.data?.area_work);
  const areaPrev = areaDonePct(previous.data?.area_work);
  const areaVsPrev = areaDoneDelta(aw, previous.data?.area_work);
  const countDone = countShareText(done, ob.total);
  const overdueWorkHa = hasArea(aw) ? haOf(aw.overdue_microha) : null;
  // Главное число «После срока» — ЗЕМЛЯ (каждый гектар один раз, журнал №690):
  // сумма гектаров работ бывает больше всей земли цикла. Доля — та же
  // величина, что метрика area.overdue_land_pct (сверено тестом паритета).
  const overdueHaWeek = value(weekAgo.data?.overdue_area) === null ? null : value(weekAgo.data?.overdue_area)! / 1e6;
  const landTotal = pic.area.total_microha === null ? null : pic.area.total_microha / 1e6;
  const overdueLandPct = overdueHa !== null && landTotal ? (100 * overdueHa) / landTotal : null;
  const overdueDelta = countDelta(overdueNow, value(weekAgo.data?.overdue));
  const waterShare = water?.sharePct ?? null;
  const sparkShares = series.filter(p => p.share !== null).slice(-12).map(p => p.share!);
  const area = pic.area.total_microha === null ? null : pic.area.total_microha / 1e6;
  const lateCount = openAssignments.filter(a => isLate(a, today)).length;
  const actorsTotal = pic.actors.total;

  const massifOptions = [{ id: 'all', name: t(m.optionAll) }, ...[...(massifs.data?.items ?? [])].map(row => ({ id: String(row.id), name: massifLabel(row.canonical_name) })).sort((a, b) => a.name.localeCompare(b.name, runtimeLocale()))];
  const campaignTitle = campaign ? workName(campaign.title) : null;
  const assignmentsTotal = assignments.data ? (assignments.data.total ?? assignments.data.items.length) : null;
  const qTotal = quarantine.data?.contours_total ?? null;
  // Конец окна выбранного агроцикла — в «Скоро», если он в ближайший месяц.
  const cycleEnd = cycle.window_to && cycle.window_to >= asOf && daysBetween(asOf, cycle.window_to) <= CYCLE_END_DAYS ? cycle.window_to : null;
  const sparkUnit = areaSeries ? t(m.sparkByArea) : t(m.sparkByCount);
  const perWeek = t(m.perWeek);
  const ppUnit = ` ${t(m.ppUnit)}`;
  // Изменение доли в п.п. со знаком: «+1,5 п.п.».
  const ppText = (v: number) => t(m.ppValue, { value: `${v > 0 ? '+' : ''}${fmtNum(v, 0, 1)}` });
  const worksOf = (done: number, total: number) => t(m.worksOfTotal, { done: num(done), total });
  const doneLabel = split ? doneShareLabel() : reportShareLabel();
  // Строка агроцикла: участники и земля — по размещению (cycle.*), весь район.
  const agroKpis = agroRow?.kpis;
  const cycleFarms = massifId === null ? kpi(agroKpis, 'cycle.farms') : null;
  const cycleLand = massifId === null ? kpi(agroKpis, 'cycle.land_ha') : null;

  const planText = agroRow ? planLine(agroKpis) : '';
  const worksDoneText = worksOf(done, ob.total);
  const districtName = t(m.districtName);
  const scopeText = cycleFarms !== null
    ? t(m.scopeCycle, {
      district: districtName,
      farms: t(WORDS.farm, { count: cycleFarms }),
      land: cycleLand === null ? t(m.landCycleNone) : t(m.landCycle, { ha: num(cycleLand) }),
      date: dateShort(asOf),
    })
    : t(m.scopeWorks, {
      district: districtName,
      farms: t(WORDS.farm, { count: actorsTotal }),
      land: area === null ? t(m.landWorksNone) : t(m.landWorks, { ha: num(area) }),
      date: dateShort(asOf),
    });
  const pageTitle = massifName ? t(m.titleMassif, { massif: massifName }) : t(m.titleMain);

  return (
    <div
      className={`district ${print ? 'district--print' : ''}`}
      // i18n-ignore: служебная метка области для ИИ-гида, не текст интерфейса
      data-agent-scope={`Цикл: ${name} · Массив: ${massifName ?? 'все'} · Дата среза: ${dateShort(asOf)}`}
    >
      {/* 1. шапка */}
      <PageHeader
        title={print ? t(m.titlePrint, { title: pageTitle }) : pageTitle}
        actions={!print ? (
          <div className="district-controls district-noprint" data-guide="scope">
            <ChoiceSelect label={t(m.cycleControl)} value={cycle.cycle} options={cycleList.map(c => ({ id: c.cycle, name: cycleName(c), description: c.status === 'active' ? t(m.optionCurrent) : undefined }))} onChange={v => setParam('cycle', v)} />
            <ChoiceSelect label={t(m.massifControl)} value={massifId === null ? 'all' : String(massifId)} options={massifOptions} onChange={v => ctx.setViewFilter('massifId', v === 'all' ? null : Number(v))} />
            <button type="button" className="astra-button" onClick={() => setParam('mode', 'print')}><Printer size={16} /> {t(m.printButton)}</button>
          </div>
        ) : (
          <div className="district-controls district-screen-only">
            <button type="button" className="astra-button" onClick={() => window.print()}><Printer size={16} /> {t(m.printNow)}</button>
            <button type="button" className="astra-button" onClick={() => setParam('mode', null)}>{t(m.backToOverview)}</button>
          </div>
        )}
      >
        <p className="district-scope">
          {print ? t(m.scopePrint, { name, scope: scopeText }) : scopeText}
          <InfoPopover icon="info" label={t(m.scopeInfoLabel)}>{[
            cycleFarms !== null ? t(m.scopeInfoFarmsLand) : null,
            t(m.scopeInfoMain, { name, date: dateShort(asOf) }),
            areaSeries ? t(m.scopeInfoChartsArea) : t(m.scopeInfoChartsCount),
            t(m.scopeInfoTail),
          ].filter(Boolean).join(' ')}</InfoPopover>
        </p>
      </PageHeader>

      {/* 2. пять показателей — у каждого основа «из N» */}
      <section className="district-section" data-guide="kpi">
        <h2 className="district-section__title">{t(m.sectionKpi, { name, date: dateShort(asOf) })}</h2>
        <div className="district-kpis">
          {areaPct !== null && aw ? (
            <KpiCard
              label={doneShareLabel()}
              icon={ListChecks}
              value={fmtNum(areaPct, 1)}
              unit={t(m.doneUnitArea)}
              to={link('/cycles')}
              action="navigate"
              note={<span className="district-kpi-note">{!print && sparkShares.length > 1 ? <Sparkline values={sparkShares} width={72} height={20} label={t(m.sparkLabel, { unit: sparkUnit })} /> : null}<span>{t(m.doneNoteOf, { done: haWhole(haOf(aw.done_microha)), total: haWhole(haOf(aw.total_microha)) })} · </span>{failed(weekAgo) ? <span className="district-delta">{t(m.perWeekFailed)}</span> : <Delta value={areaWeek} unit={ppUnit} digits={1} sense="up-good" suffix={perWeek} />}{agroRow ? <span className="district-kpi-plan">{planText}</span> : null}</span>}
              info={<InfoPopover icon="info" label={t(m.doneInfoLabelArea, { label: doneShareLabel() })}>{[
                t(m.doneInfoAreaMain, {
                  share: pct(areaPct, 1),
                  name,
                  date: dateShort(asOf),
                  done: num(haOf(aw.done_microha)),
                  total: num(haOf(aw.total_microha)),
                  hasPlan: agroRow ? 'yes' : 'no',
                  plan: planText,
                  inProgress: pct(aw.in_progress_pct, 1),
                  noReport: pct(aw.no_report_pct, 1),
                  worksDone: worksDoneText,
                  countShare: countDone ?? 'none',
                  weekDate: weekAgoDate ? dateShort(weekAgoDate) : '—',
                }),
                prevCycle && prevAsOf
                  ? t(m.doneInfoPrevCycle, {
                    cycle: cycleName(prevCycle),
                    date: dateShort(prevAsOf),
                    value: areaPrev === null ? t(m.noData) : t(m.doneInfoPrevValueArea, { share: pct(areaPrev, 1) }),
                    delta: areaVsPrev === null ? 'none' : ppText(areaVsPrev),
                  })
                  : t(m.doneInfoNoPrev),
                t(m.doneInfoSpark, { unit: sparkUnit }),
              ].join(' ')}</InfoPopover>}
            />
          ) : (
            <KpiCard
              label={doneLabel}
              icon={ListChecks}
              value={reportShareValue(done, ob.total)}
              unit={t(m.doneUnitCount)}
              to={link('/cycles')}
              action="navigate"
              note={<span className="district-kpi-note">{!print && sparkShares.length > 1 ? <Sparkline values={sparkShares} width={72} height={20} label={t(m.sparkLabel, { unit: sparkUnit })} /> : null}<span>{t(m.doneNoteCountOf, { done: num(done), total: ob.total })} · </span>{failed(weekAgo) ? <span className="district-delta">{t(m.perWeekFailed)}</span> : <Delta value={week.delta} unit={ppUnit} digits={1} sense="up-good" suffix={perWeek} />}</span>}
              info={<InfoPopover icon="info" label={t(m.doneInfoLabelArea, { label: doneLabel })}>{[
                t(m.doneInfoCountNoArea),
                split
                  ? t(m.doneInfoCountSplit, { done: num(done), total: ob.total, date: dateShort(asOf), inProgress: num(inProgress), noReport: num(noReport) })
                  : t(m.doneInfoCountReported, { reported: num(ob.reported), total: ob.total, date: dateShort(asOf) }),
                prevCycle && prevAsOf
                  ? t(m.doneInfoPrevCycle, {
                    cycle: cycleName(prevCycle),
                    date: dateShort(prevAsOf),
                    value: prevShare === null ? t(m.noData) : pct(prevShare),
                    delta: vsPrev.delta === null ? 'none' : ppText(vsPrev.delta),
                  })
                  : null,
              ].filter(Boolean).join(' ')}</InfoPopover>}
            />
          )}
          <KpiCard
            label={t(m.overdueTitle)}
            icon={CalendarClock}
            value={overdueHa !== null ? num(overdueHa) : num(overdueNow)}
            unit={overdueHa !== null ? t(m.overdueUnitLand) : t(m.worksWord, { count: overdueNow ?? 0 })}
            tone={overdueNow ? 'warning' : undefined}
            action="filter"
            onClick={() => openProblems(KPI_TAB.overdue)}
            actionLabel={t(m.overdueActionLabel)}
            note={<span className="district-kpi-note">{overdueHa !== null
              ? [
                area !== null && overdueLandPct !== null ? t(m.overdueNoteLandOf, { land: haWhole(area), share: pct(overdueLandPct, 1) }) : null,
                t(WORDS.obligation, { count: overdueNow ?? 0 }),
              ].filter(Boolean).join(' · ')
              : t(m.overdueNoteNoReport, { noArea: noArea() })} <span>· <Delta value={overdueHa !== null ? countDelta(Math.round(overdueHa), overdueHaWeek === null ? null : Math.round(overdueHaWeek)) : overdueDelta} unit={overdueHa !== null ? ` ${t(m.haUnit)}` : ''} sense="up-bad" suffix={perWeek} /></span></span>}
            info={<InfoPopover icon="info" label={t(m.overdueInfoLabel)}>{[
              t(m.overdueInfoHead),
              overdueHa !== null
                ? t(m.overdueInfoLand, {
                  ha: haWhole(overdueHa),
                  hasTotal: area !== null ? 'yes' : 'no',
                  land: area !== null ? haWhole(area) : '',
                  hasShare: overdueLandPct !== null ? 'yes' : 'no',
                  share: overdueLandPct !== null ? pct(overdueLandPct, 1) : '',
                  works: num(overdueNow),
                  hasWorkHa: overdueWorkHa !== null ? 'yes' : 'no',
                  workHa: overdueWorkHa !== null ? num(overdueWorkHa) : '',
                })
                : t(m.overdueInfoCount),
              t(m.overdueInfoTail),
            ].join(' ')}</InfoPopover>}
          />
          <KpiCard
            label={t(m.waterTitle)}
            icon={Droplets}
            value={waterEvents ? m3Compact(water!.deliveredM3!) : waterShare === null ? '—' : pct(waterShare, 1)}
            action="filter"
            onClick={() => openProblems(KPI_TAB.water)}
            actionLabel={t(m.waterActionLabel)}
            note={waterEvents ? t(m.waterNoteDoneLand, { pct: doneLand === null ? '—' : pct(doneLand, 1) }) : deficit === null ? t(m.waterNoteUnknown, { words: waterShareWords() }) : deficit > 0 ? <span className="district-warn">{t(m.waterNoteDeficit, { words: waterShareWords(), m3: m3Compact(deficit) })}</span> : t(m.waterNoteNone, { words: waterShareWords() })}
            info={<InfoPopover icon="info" label={t(m.waterInfoLabel)}>{waterEvents ? t(m.waterInfoEvents) : <>{waterShareMethod()} {pic.water_note}</>}</InfoPopover>}
          />
          <KpiCard
            label={t(m.quarantineTitle)}
            icon={ShieldAlert}
            value={qNow ? num(qNow.red + qNow.yellow) : isLoading(quarantine) ? <Skeleton variant="block" width={72} height={30} /> : '—'}
            unit={qNow ? t(m.quarantineUnit, { count: qNow.red + qNow.yellow }) : undefined}
            action="filter"
            onClick={() => openProblems(KPI_TAB.quarantine)}
            actionLabel={t(m.quarantineActionLabel)}
            note={<span className="district-kpi-note">{qNow ? t(m.quarantineNoteOf, { total: num(qTotal), red: num(qNow.red), yellow: num(qNow.yellow) }) : failed(quarantine) ? t(m.quarantineLoadError) : <Skeleton width={110} className="skel--caption" />} <span>· <Delta value={qNow && qThen ? qNow.red + qNow.yellow - (qThen.red + qThen.yellow) : null} unit="" sense="up-bad" suffix={perWeek} /></span></span>}
            info={<InfoPopover icon="info" label={t(m.quarantineInfoLabel)}>{t(m.quarantineInfo)}</InfoPopover>}
          />
          <KpiCard
            label={t(m.assignTitle)}
            icon={ClipboardList}
            value={assignmentItems ? num(openAssignments.length) : isLoading(assignments) ? <Skeleton variant="block" width={56} height={30} /> : '—'}
            unit={assignmentItems ? t(m.assignUnitOpenOf, { total: num(assignmentsTotal) }) : failed(assignments) ? t(m.assignUnitFailed) : undefined}
            to={link('/ops/assignments')}
            action="navigate"
            note={!assignmentItems && isLoading(assignments) ? <Skeleton width={120} className="skel--caption" /> : <span className="district-kpi-note">{lateCount ? <span className="district-warn">{t(m.assignLate, { count: num(lateCount) })}</span> : t(m.assignLate, { count: '0' })}</span>}
            info={<InfoPopover icon="info" label={t(m.assignInfoLabel)}>{t(m.assignInfoOpen)}</InfoPopover>}
          />
        </div>
      </section>

      {/* 3. находки сканера (ADR-0014): 3–5 главных, ссылки на экраны */}
      <div data-guide="insights">
        <InsightsBlock params={base} ctxKey={ctx.key} linkSearch={() => ctx.linkSearch()} />
      </div>

      {/* 4. «Сейчас в районе»: агроциклы и этапы, работы в поле, скоро */}
      <div data-guide="now">
        <NowSummary
          asOf={asOf}
          cycles={activeCycles}
          selected={cycle.cycle}
          onCycle={key => setParam('cycle', key)}
          cycleName={name}
          cycleEnd={cycleEnd}
          plan={planText}
          windows={fieldWindows}
          soon={soonWorks}
          calendarHref={link('/cycles')}
          print={print}
        />
      </div>

      {/* 5. Где нужно разобраться — главный рабочий блок, сразу под сводкой */}
      <div className={print ? 'district-noprint' : ''} data-guide="problems">
        <ProblemsBoard
          id={PROBLEMS_ID}
          scope={problemScope}
          season={cycleId ? cycleId.split(':')[0] ?? null : null}
          massifId={massifId}
          tab={tab}
          onTab={setTab}
          farmId={search.get('farm')}
          onFarm={id => setParam('farm', id)}
          waterGapByFarm={waterGapByFarm}
          districtFarms={district.data.summary?.by_farm ?? null}
        />
      </div>

      {/* 6. динамика: ход работ | темп */}
      <section className="district-section">
        <h2 className="district-section__title">{t(m.sectionDynamics)}</h2>
        <Grid cols="7-5">
          <div data-guide="season">
            <SeasonChart points={series} cycleName={name} prevName={prevCycle ? cycleName(prevCycle) : null} total={ob.total} areaHa={areaSeries && aw ? haOf(aw.total_microha) : null} demo={demo} print={print} error={pic.summary ? null : { message: t(m.seasonNoSummary) }} onRetry={() => { district.reload(); massifPicture.reload(); }} />
          </div>
          <div data-guide="pace">
            {/* Ошибка связи ≠ «кампаний нет» (agroai-6al.35): «нет» говорится,
                только когда картина прочитана и сервер назвал ноль кампаний. */}
            {campaign && failed(daily)
              ? <Card title={t(m.paceTitle, { title: campaignTitle ?? campaign.title })}><LoadError compact what={t(m.paceWhat)} error={daily.error} onRetry={daily.reload} /></Card>
              : campaign && isLoading(daily)
                ? <SkeletonChart label={t(m.paceSkeleton)} title={t(m.paceTitle, { title: campaignTitle ?? campaign.title })} height={280} bars={10} captionLines={2} />
              : campaign && pace
                ? <PaceChart title={campaignTitle ?? campaign.title} pace={pace} total={campaign.obligations.total} windowTo={campaign.window_to ?? asOf} demo={demo} print={print} />
                : <EmptyBlock title={t(m.paceTitlePlain)} text={t(m.paceNoCampaign)} />}
          </div>
        </Grid>
      </section>

      {/* 7. справка: шаги поручений, хозяйства после срока (дубль вкладки
          «Сроки» — свёрнут), работы по источнику (свёрнуты; план — знаменатель
          «Выполнено», предписания и добровольные — отдельно, строка районная). */}
      <section className="district-section">
        <h2 className="district-section__title">{t(m.sectionRef)}</h2>
        <div data-guide="assignments">
          <DecisionFlow counts={flowCounts} hrefFor={status => link('/ops/assignments', { status })} allHref={link('/ops/assignments')} late={lateCount} error={failed(assignments) ? assignments.error : null} onRetry={assignments.reload} />
        </div>
        <div data-guide="farms">
          <LateFarms ranked={ranking.rows} eligible={ranking.eligible} farmHref={id => link(`/farms/${id}`)} allHref={link('/farms', { sort: 'overdueHa' })} />
        </div>
        {agroRow?.sources?.length ? (
          <div data-guide="sources" className="district-sources">
            <Disclosure label={massifId === null ? t(m.sourcesTitle) : t(m.sourcesTitleDistrict)} summary={t(m.sourcesSummary)}>
              <p className="district-chart__caption">{t(m.sourcesInfo)}</p>
              <CycleSources sources={agroRow.sources} compact={print} />
            </Disclosure>
          </div>
        ) : null}
      </section>

      {!print ? <p className="district-foot">{demo ? t(m.footScopeDemo, { name, date: dateShort(asOf) }) : t(m.footScope, { name, date: dateShort(asOf) })}</p> : null}
    </div>
  );
}
